import { GetProductUrl, getRelatedProducts } from '@app/logic/products';
import { useProductMedia } from '@app/utils/hooks';
import { isDefined, isNull } from '@app/utils/is';
import { AnyMedia, DeliveryWindow, Product } from '@typings';
import React from 'react';
import { useNavigate } from 'react-router-dom';

interface Context {
  productDetails: Product.Full;
  activeVariant: Product.Standard;
  activeMediaIndex: number;
  medias: AnyMedia[];
  variants: Product.Standard[];
  setActiveMedia: (media: AnyMedia) => void;
  handleMediaIndexChange: (mediaIndex: number) => void;
  isLookbook?: boolean;
}

const ProductDetailsVariantContext = React.createContext<Context | null>(null);

export const useProductDetailsVariantContext = (): Context => {
  const context = React.useContext(ProductDetailsVariantContext);
  if (isNull(context)) {
    throw new Error('ProductDetailsVariantContext can not be used outside the scope of ProductDetailsVariantContextProvider');
  }

  return context;
};

interface Props {
  productDetails: Product.Full;
  getProductUrl: GetProductUrl;
  delwinIds?: DeliveryWindow.Id[];
  productId: string | undefined;
  variantId: string | undefined;
  isLookbook?: boolean;
}

export const ProductDetailsVariantContextProvider = ({
  children,
  productDetails,
  getProductUrl,
  delwinIds,
  productId,
  variantId,
}: React.WithChildren<Props>) => {
  const navigate = useNavigate();
  const { medias } = useProductMedia({ productDetails });

  const variants = React.useMemo(() => {
    const relatedVariants = getRelatedProducts(productDetails);

    if (!isDefined(delwinIds)) {
      return [productDetails, ...relatedVariants];
    }

    return [
      productDetails,
      ...relatedVariants.filter(variant =>
        variant.deliveryWindows.some(delwin => {
          // wrong type as string in product.deliveryWindows. Should be a number
          return delwinIds.includes(delwin.toString());
        }),
      ),
    ];
  }, [delwinIds, productDetails]);

  const getInitialActiveVariant = () => {
    return variants.find(variant => variant.variant === variantId) ?? variants[0] ?? productDetails;
  };

  const getInitialActiveMediaIndex = () => {
    const index = medias.findIndex(media => media.variantId === getInitialActiveVariant().variant);

    return index === -1 ? 0 : index;
  };

  const [activeVariant, setActiveVariant] = React.useState<Product.Standard>(getInitialActiveVariant);
  const [activeMediaIndex, setActiveMediaIndex] = React.useState(getInitialActiveMediaIndex);

  React.useEffect(() => {
    setActiveVariant(getInitialActiveVariant());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDetails.product, variantId]);

  React.useEffect(() => {
    setActiveMediaIndex(getInitialActiveMediaIndex());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const setActiveMedia = (media: AnyMedia) => {
    const mediaIndex = medias.findIndex(mediaItem => mediaItem.src === media.src && mediaItem.variantId === media.variantId);

    if (mediaIndex !== -1) {
      setActiveMediaIndex(mediaIndex);
    }
  };

  const handleMediaIndexChange = React.useCallback(
    (mediaIndex: number) => {
      const medium = medias[mediaIndex];

      if (!isDefined(medium)) {
        return;
      }

      setActiveMediaIndex(mediaIndex);
      if (productDetails.product !== medium.productId) {
        return;
      }
      const newVariant = variants.find(variant => variant.variant === medium.variantId);

      if (!isDefined(newVariant)) {
        return;
      }

      setActiveVariant(newVariant);
      navigate(
        getProductUrl({
          productId: productDetails.product,
          variantId: newVariant.variant,
        }),
        { replace: true },
      );
    },
    [getProductUrl, medias, navigate, productDetails.product, variants],
  );

  return (
    <ProductDetailsVariantContext.Provider
      value={{ activeMediaIndex, activeVariant, handleMediaIndexChange, medias, productDetails, setActiveMedia, variants }}
    >
      {children}
    </ProductDetailsVariantContext.Provider>
  );
};
